import { memo, type ReactNode } from 'react';

import { useAuiState, type ToolCallMessagePart } from '@assistant-ui/react';
import { takeWhile } from 'lodash';
import { ScrollTextIcon } from 'lucide-react';

import type { ApprovalPartState } from '@/components/assistant-ui/approval/approval-state';
import { GenericApprovalCard } from '@/components/assistant-ui/approval/generic-approval-card';
import {
  isErrorResult,
  isSkillCall,
  SKILL_TOOL_NAME,
} from '@/components/assistant-ui/message-part-utils';
import { ToolDebugInfo, ToolDebugLabel } from '@/components/assistant-ui/tool-debug-info';
import { useToolGroup } from '@/components/assistant-ui/tool-group-provider';
import { buildToolCallStatus } from '@/components/assistant-ui/tool-status';
import { useDebugMode } from '@/components/providers/debug-mode-provider';
import { LinkToolNameProvider } from '@/components/ui/link-location-context';

interface ToolFallbackProps {
  toolName: string;
  args: unknown;
  result?: unknown;
  isError?: boolean;
  toolCallId: string;
  argsText: string;
}

// Gates are asked sequentially, so an unasked sibling may be queued rather than running.
function useToolIsGated(): boolean {
  return useAuiState(({ message }) =>
    (message.content as readonly { type: string; toolName?: string; approval?: unknown }[]).some(
      part => part.type === 'tool-call' && !!part.approval,
    ),
  );
}

/** The run of consecutive skill calls this call starts, or [] when it is not the run's first. */
export function findSkillRun({
  content,
  toolCallId,
}: {
  content: readonly unknown[];
  toolCallId: string;
}): ToolCallMessagePart[] {
  const index = content.findIndex(part => isSkillCall(part) && part.toolCallId === toolCallId);
  if (index < 0 || isSkillCall(content[index - 1])) return [];
  return takeWhile(content.slice(index), isSkillCall) as ToolCallMessagePart[];
}

/** Consecutive skill loads render as one row naming the skills; the first call of a run owns it.
 *  Not expandable: hosted responses strip the procedures, so there is no result to show. */
function SkillsDebugInfo({ toolCallId }: { toolCallId: string }) {
  const content = useAuiState(({ message }) => message.content);
  const isGated = useToolIsGated();
  const calls = findSkillRun({ content, toolCallId });
  if (!calls.length) return null;

  const statuses = calls.map(call =>
    buildToolCallStatus({
      result: call.result,
      hasError: call.isError || isErrorResult(call.result),
      isGated,
    }),
  );
  const names = calls.map(call => String((call.args as { name?: unknown })?.name ?? '?'));
  return (
    <ToolDebugLabel
      label={`${names.length > 1 ? 'Skills' : 'Skill'} loaded: ${names.join(', ')}`}
      icon={ScrollTextIcon}
      status={statuses.find(status => status.type !== 'complete') ?? statuses[0]}
    />
  );
}

const ToolFallback = memo(function ToolFallback({
  toolName,
  args,
  result,
  isError,
  toolCallId,
}: ToolFallbackProps) {
  const messageStatus = useAuiState(({ message }) => message.status?.type);
  const { isHandledTool } = useToolGroup();
  const { isDebugMode } = useDebugMode();
  const isGated = useToolIsGated();
  const status = buildToolCallStatus({
    result,
    hasError: isError || isErrorResult(result),
    isGated,
  });

  // Stopped turns keep their debug rows: that is when they are most needed.
  if (messageStatus === 'incomplete' && !isDebugMode) return null;
  if (isHandledTool(toolName) && !isDebugMode) return null;
  if (toolName === SKILL_TOOL_NAME)
    return isDebugMode && <SkillsDebugInfo toolCallId={toolCallId} />;

  // Debug rows render at their own part position so they list calls in the order they ran; tool
  // groups render only carousels, and a custom tool UI wraps its own ToolDebugInfo.
  return (
    <ToolDebugInfo toolName={toolName} args={args} result={result} status={status}>
      {null}
    </ToolDebugInfo>
  );
});

export function ToolCallPart({ part }: { part: ToolCallMessagePart & { toolUI?: ReactNode } }) {
  const approval = (part as { approval?: ApprovalPartState }).approval;
  return (
    <div data-tts-ignore>
      <LinkToolNameProvider value={part.toolName}>
        {part.toolUI ??
          (approval ? (
            <GenericApprovalCard
              toolName={part.toolName}
              toolCallId={part.toolCallId}
              args={part.args}
              approval={approval}
              status={buildToolCallStatus({
                result: part.result,
                hasError: part.isError || isErrorResult(part.result),
                approval,
              })}
              result={part.result}
            />
          ) : (
            <ToolFallback
              toolName={part.toolName}
              args={part.args}
              result={part.result}
              isError={part.isError}
              toolCallId={part.toolCallId}
              argsText={part.argsText}
            />
          ))}
      </LinkToolNameProvider>
    </div>
  );
}
